/**
 * API Request Types
 *
 * This file contains type definitions for API request configuration,
 * metadata, context, lifecycle management, and deduplication. These types
 * are used by the Request Coordinator, Request Tracker, and related components.
 *
 * @module types/api/request
 */

import type {
  CircuitBreakerState,
  CircuitState,
} from './circuit-breaker.types';
import type { ErrorClassification } from './error.types';
import type { PerformanceMetrics } from './integration.types';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

// Import from centralized types locations

// Import CircuitState for deduplication types

// =============================================================================
// REQUEST CONFIGURATION
// =============================================================================

/**
 * Extended Axios request configuration with metadata and deduplication options.
 *
 * This interface extends the standard AxiosRequestConfig with additional
 * properties used by the request coordination system.
 *
 * @example
 * ```typescript
 * const config: RequestConfig = {
 *   url: '/api/users',
 *   method: 'GET',
 *   metadata: {
 *     requestId: 'req_123',
 *     timestamp: dayjs().valueOf(),
 *     skipAuth: false,
 *   },
 *   deduplication: {
 *     enabled: true,
 *     ttl: 60000,
 *   },
 * };
 * ```
 */
export interface RequestConfig extends AxiosRequestConfig {
  /**
   * Request metadata for tracking and coordination.
   */
  metadata?: CoreRequestMetadata;

  /**
   * Deduplication options for this specific request.
   * Overrides global deduplication settings when provided.
   */
  deduplication?: DeduplicationOptions;
}

// =============================================================================
// REQUEST METADATA
// =============================================================================

/**
 * Comprehensive metadata attached to API requests for tracking, debugging,
 * and coordination purposes.
 *
 * This is the full metadata interface used by the Request Coordinator.
 * For the simpler client-level metadata, see RequestMetadata in client.types.ts.
 *
 * @example
 * ```typescript
 * const metadata: CoreRequestMetadata = {
 *   requestId: 'req_1234567890_abc123',
 *   timestamp: dayjs().valueOf(),
 *   skipAuth: false,
 *   retryCount: 0,
 *   isRetry: false,
 *   endpoint: '/api/users',
 *   method: 'GET',
 * };
 * ```
 */
export interface CoreRequestMetadata {
  /**
   * Unique identifier for the request.
   * Used for request tracking, logging, deduplication, and debugging.
   */
  requestId: string;

  /**
   * Unix timestamp (milliseconds) when the request was initiated.
   */
  timestamp: number;

  /**
   * Unix timestamp (milliseconds) of the caller's send. Unlike `timestamp`,
   * which the retry engine re-stamps per attempt, this stays put across
   * retries and the auth-refresh replay, so a paywall presenter can tell
   * which screen asked (`enrichApiErrorWithAxiosContext` copies it onto the
   * typed error as `requestStartedAt`).
   */
  startedAt?: number;

  /**
   * Whether to skip authentication header injection for this request.
   * Useful for public endpoints or auth-related endpoints.
   */
  skipAuth?: boolean;

  /**
   * Current retry attempt number (0-indexed).
   * Incremented by the retry engine on each retry.
   */
  retryCount?: number;

  /**
   * Whether this request is a retry of a failed request.
   */
  isRetry?: boolean;

  /**
   * Request ID of the original request if this is a retry.
   */
  parentRequestId?: string;

  /**
   * The API endpoint being requested (for logging/metrics).
   */
  endpoint?: string;

  /**
   * HTTP method being used (for logging/metrics).
   */
  method?: string;

  /**
   * Key used for request deduplication.
   * Requests with the same key may be deduplicated.
   */
  deduplicationKey?: string;

  /**
   * Whether this request was identified as a duplicate.
   */
  isDuplicate?: boolean;

  /**
   * Allow additional custom metadata properties.
   */
  [key: string]: unknown;
}

// =============================================================================
// REQUEST STATUS
// =============================================================================

/**
 * Enumeration of possible request lifecycle states.
 *
 * These states track the progress of a request through the coordination system.
 *
 * @example
 * ```typescript
 * if (context.status === RequestStatus.RETRYING) {
 *   console.log(`Retrying request ${context.id}...`);
 * }
 * ```
 */
export enum RequestStatus {
  /** Request is queued but not yet started */
  PENDING = 'pending',

  /** Request is currently being executed */
  IN_PROGRESS = 'in_progress',

  /** Request failed and is being retried */
  RETRYING = 'retrying',

  /** Request completed successfully */
  COMPLETED = 'completed',

  /** Request failed after all retry attempts exhausted */
  FAILED = 'failed',

  /** Request was cancelled before completion */
  CANCELLED = 'cancelled',

  /** Request blocked by circuit breaker */
  CIRCUIT_BROKEN = 'circuit_broken',

  /** Request was deduplicated (response from cache) */
  DEDUPLICATED = 'deduplicated',
}

// =============================================================================
// REQUEST CONTEXT
// =============================================================================

/**
 * Complete context for a request throughout its lifecycle.
 *
 * This interface tracks all aspects of a request from initiation
 * through completion or failure, including retries and performance metrics.
 *
 * @example
 * ```typescript
 * const context: RequestContext = {
 *   id: 'req_123',
 *   config: { url: '/api/users', method: 'GET' },
 *   startTime: dayjs().valueOf(),
 *   status: RequestStatus.IN_PROGRESS,
 *   retryCount: 0,
 *   maxRetries: 3,
 *   metadata: {
 *     requestId: 'req_123',
 *     timestamp: dayjs().valueOf(),
 *   },
 * };
 * ```
 */
export interface RequestContext {
  /**
   * Unique identifier for this request context.
   */
  id: string;

  /**
   * The request configuration.
   */
  config: RequestConfig;

  /**
   * Unix timestamp when the request started.
   */
  startTime: number;

  /**
   * Unix timestamp when the request completed (if finished).
   */
  endTime?: number;

  /**
   * Current status of the request.
   */
  status: RequestStatus;

  /**
   * Number of retry attempts made for this request.
   */
  retryCount: number;

  /**
   * Maximum number of retries allowed for this request.
   */
  maxRetries: number;

  /**
   * The last error encountered, if any.
   */
  lastError?: Error;

  /**
   * The successful response, if completed.
   */
  response?: AxiosResponse;

  /**
   * Request metadata for tracking and coordination.
   */
  metadata: CoreRequestMetadata;

  /**
   * Performance metrics for this request.
   */
  performanceMetrics?: PerformanceMetrics;

  /**
   * Deduplication entry if this request was deduplicated.
   */
  deduplicationEntry?: DeduplicationEntry;

  /**
   * Error classification if the request failed.
   */
  classification?: ErrorClassification;
}

// =============================================================================
// REQUEST UPDATE
// =============================================================================

/**
 * Partial update to a request context.
 *
 * Used to update specific fields of a RequestContext without
 * needing to provide all values.
 *
 * @example
 * ```typescript
 * const update: RequestUpdate = {
 *   status: RequestStatus.COMPLETED,
 *   response: axiosResponse,
 *   performanceMetrics: metrics,
 * };
 * tracker.updateRequest(requestId, update);
 * ```
 */
export interface RequestUpdate {
  /**
   * Updated request status.
   */
  status?: RequestStatus;

  /**
   * Updated retry count.
   */
  retryCount?: number;

  /**
   * Last error encountered.
   */
  lastError?: Error;

  /**
   * Response received.
   */
  response?: AxiosResponse;

  /**
   * Updated performance metrics.
   */
  performanceMetrics?: PerformanceMetrics;
}

// =============================================================================
// REQUEST STATE
// =============================================================================

/**
 * Aggregate in-memory state tracked for all requests, including active and
 * completed requests, circuit breaker states, and performance metrics.
 */
export interface RequestState {
  activeRequests: Map<string, RequestContext>;
  completedRequests: Map<string, RequestContext>;
  circuitBreakerStates: Map<string, CircuitBreakerState>;
  performanceMetrics: Map<string, PerformanceMetrics>;
  lastCleanup: number;
}

// =============================================================================
// DEDUPLICATION TYPES
// =============================================================================

/**
 * Key components used to identify duplicate requests.
 *
 * The deduplication system generates a hash from these components to determine
 * if two requests are functionally equivalent and can share a single response.
 *
 * @example
 * ```typescript
 * const key: DeduplicationKey = {
 *   method: 'GET',
 *   url: '/api/users/123',
 *   params: { include: 'profile' },
 *   retryContext: {
 *     attempt: 0,
 *     parentRequestId: 'req_abc123',
 *   },
 * };
 * ```
 */
export interface DeduplicationKey {
  /**
   * HTTP method of the request.
   */
  method: string;

  /**
   * Request URL (endpoint path).
   */
  url: string;

  /**
   * Query parameters that affect the response.
   */
  params?: Record<string, unknown>;

  /**
   * Headers that affect the response (if includeHeaders is enabled).
   */
  headers?: Record<string, string>;

  /**
   * Request body (if includeBody is enabled).
   */
  body?: unknown;

  /**
   * Retry context for distinguishing retry attempts.
   */
  retryContext?: {
    /**
     * Current retry attempt number.
     */
    attempt: number;
    /**
     * ID of the original request being retried.
     */
    parentRequestId: string;
  };

  /**
   * Current circuit breaker state (if applicable).
   */
  circuitBreakerState?: CircuitState;
}

/**
 * Entry in the deduplication cache representing an in-flight or cached request.
 *
 * When a duplicate request is detected, subscribers are added to the existing
 * entry's subscriber set to receive the response when it completes.
 *
 * @example
 * ```typescript
 * const entry: DeduplicationEntry = {
 *   key: 'GET:/api/users:abc123',
 *   promise: fetchPromise,
 *   requestId: 'req_original',
 *   createdAt: dayjs().valueOf(),
 *   expiresAt: dayjs().valueOf() + 60000,
 *   subscribers: new Set(),
 *   metadata: {
 *     method: 'GET',
 *     url: '/api/users',
 *   },
 * };
 * ```
 */
export interface DeduplicationEntry {
  /**
   * Hashed key string identifying this request.
   */
  key: string;

  /**
   * The promise that will resolve with the response.
   * Subscribers wait on this promise.
   */
  promise: Promise<unknown>;

  /**
   * ID of the original request that created this entry.
   */
  requestId: string;

  /**
   * Timestamp when this entry was created.
   */
  createdAt: number;

  /**
   * Timestamp when this entry expires and should be removed.
   */
  expiresAt: number;

  /**
   * Set of subscribers waiting for this request to complete.
   */
  subscribers: Set<{
    /**
     * Callback to resolve the subscriber's promise with the response.
     */
    resolve: (value: unknown) => void;
    /**
     * Callback to reject the subscriber's promise with an error.
     */
    reject: (error: unknown) => void;
    /**
     * ID of the subscriber's request.
     */
    requestId: string;
  }>;

  /**
   * Metadata about the deduplicated request.
   */
  metadata: {
    /**
     * HTTP method of the request.
     */
    method: string;
    /**
     * Request URL.
     */
    url: string;
    /**
     * Retry attempt number if this is a retry.
     */
    retryAttempt?: number;
    /**
     * Circuit breaker state when the request was made.
     */
    circuitBreakerState?: CircuitState;
  };
}

/**
 * Configuration options for the request deduplication system.
 *
 * These options control how duplicate requests are identified and handled.
 *
 * @example
 * ```typescript
 * const options: DeduplicationOptions = {
 *   enabled: true,
 *   ttl: 30000, // 30 seconds
 *   maxCacheSize: 500,
 *   includeBody: true,
 *   includeHeaders: false,
 * };
 * ```
 */
export interface DeduplicationOptions {
  /**
   * Whether deduplication is enabled.
   * @default true
   */
  enabled?: boolean;

  /**
   * Time to live in milliseconds for cached entries.
   * @default 60000 (1 minute)
   */
  ttl?: number;

  /**
   * Maximum number of entries in the deduplication cache.
   * @default 1000
   */
  maxCacheSize?: number;

  /**
   * Whether to include request headers in the deduplication key.
   * Enable if headers affect response content.
   * @default false
   */
  includeHeaders?: boolean;

  /**
   * Whether to include request body in the deduplication key.
   * Enable for POST/PUT requests where body affects response.
   * @default true
   */
  includeBody?: boolean;

  /**
   * Whether to include retry context in the deduplication key.
   * Prevents retry attempts from being deduplicated.
   * @default true
   */
  includeRetryContext?: boolean;

  /**
   * Custom key generator function.
   * Override the default key generation logic.
   */
  keyGenerator?: (key: DeduplicationKey) => string;

  /**
   * Enable logging of deduplication events.
   * @default false in production, true in development
   */
  enableLogging?: boolean;
}

/**
 * Statistics about the deduplication system's performance.
 *
 * Used for monitoring and debugging the deduplication cache.
 *
 * @example
 * ```typescript
 * const stats: DeduplicationStats = {
 *   totalRequests: 1000,
 *   deduplicatedRequests: 150,
 *   cacheHits: 150,
 *   cacheMisses: 850,
 *   cacheSize: 45,
 *   duplicateSavings: 15, // 15% savings
 *   averageResponseTime: 250, // ms
 * };
 * ```
 */
export interface DeduplicationStats {
  /**
   * Total number of requests processed.
   */
  totalRequests: number;

  /**
   * Number of requests that were deduplicated.
   */
  deduplicatedRequests: number;

  /**
   * Number of cache hits (duplicate requests found).
   */
  cacheHits: number;

  /**
   * Number of cache misses (unique requests).
   */
  cacheMisses: number;

  /**
   * Current size of the deduplication cache.
   */
  cacheSize: number;

  /**
   * Percentage of requests that were duplicates (savings).
   */
  duplicateSavings: number;

  /**
   * Average response time in milliseconds.
   */
  averageResponseTime: number;
}

/**
 * Result of a deduplicated request operation.
 *
 * Contains the response data along with information about whether
 * the request was deduplicated and cache statistics.
 *
 * @typeParam T - Type of the response data
 *
 * @example
 * ```typescript
 * const result: DeduplicationResult<User[]> = {
 *   data: users,
 *   wasDeduplicated: true,
 *   cacheKey: 'GET:/api/users:abc123',
 *   subscribers: 3,
 *   responseTime: 45, // ms (fast because cached)
 * };
 * ```
 */
export interface DeduplicationResult<T = unknown> {
  /**
   * The response data.
   */
  data: T;

  /**
   * Whether this request was deduplicated (response from another request).
   */
  wasDeduplicated: boolean;

  /**
   * The cache key used for deduplication.
   */
  cacheKey: string;

  /**
   * Number of subscribers that received this response.
   */
  subscribers: number;

  /**
   * Time taken to receive the response in milliseconds.
   */
  responseTime: number;
}

// =============================================================================
// DEDUPLICATION DEFAULTS
// =============================================================================

/**
 * Default deduplication options.
 *
 * These values are used when deduplication options are not explicitly provided.
 *
 * @example
 * ```typescript
 * // Use defaults with some overrides
 * const options: DeduplicationOptions = {
 *   ...DEFAULT_DEDUPLICATION_OPTIONS,
 *   ttl: 30000, // Override TTL to 30 seconds
 * };
 * ```
 */
export const DEFAULT_DEDUPLICATION_OPTIONS: DeduplicationOptions = {
  enabled: true,
  ttl: 60000, // 1 minute
  maxCacheSize: 1000,
  includeHeaders: false,
  includeBody: true,
  includeRetryContext: true,
  enableLogging: false, // Will be overridden by appEnv('development') at runtime
};

// =============================================================================
// DEDUPLICATION TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid DeduplicationOptions object.
 *
 * @param value - The value to check
 * @returns True if the value is a valid DeduplicationOptions object
 *
 * @example
 * ```typescript
 * if (isDeduplicationOptions(config.deduplication)) {
 *   // config.deduplication is typed as DeduplicationOptions
 * }
 * ```
 */
export function isDeduplicationOptions(
  value: unknown
): value is DeduplicationOptions {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const options = value as Record<string, unknown>;

  // Check optional boolean fields
  if (options.enabled !== undefined && typeof options.enabled !== 'boolean') {
    return false;
  }
  if (
    options.includeHeaders !== undefined &&
    typeof options.includeHeaders !== 'boolean'
  ) {
    return false;
  }
  if (
    options.includeBody !== undefined &&
    typeof options.includeBody !== 'boolean'
  ) {
    return false;
  }
  if (
    options.includeRetryContext !== undefined &&
    typeof options.includeRetryContext !== 'boolean'
  ) {
    return false;
  }
  if (
    options.enableLogging !== undefined &&
    typeof options.enableLogging !== 'boolean'
  ) {
    return false;
  }

  // Check optional number fields
  if (options.ttl !== undefined && typeof options.ttl !== 'number') {
    return false;
  }
  if (
    options.maxCacheSize !== undefined &&
    typeof options.maxCacheSize !== 'number'
  ) {
    return false;
  }

  // Check optional function field
  if (
    options.keyGenerator !== undefined &&
    typeof options.keyGenerator !== 'function'
  ) {
    return false;
  }

  return true;
}

/**
 * Type guard to check if a value is a valid DeduplicationKey object.
 *
 * @param value - The value to check
 * @returns True if the value is a valid DeduplicationKey object
 *
 * @example
 * ```typescript
 * if (isDeduplicationKey(key)) {
 *   // key is typed as DeduplicationKey
 * }
 * ```
 */
export function isDeduplicationKey(value: unknown): value is DeduplicationKey {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const key = value as Record<string, unknown>;

  // Required fields
  if (typeof key.method !== 'string' || typeof key.url !== 'string') {
    return false;
  }

  return true;
}

/**
 * Type guard to check if a value is a valid DeduplicationStats object.
 *
 * @param value - The value to check
 * @returns True if the value is a valid DeduplicationStats object
 *
 * @example
 * ```typescript
 * if (isDeduplicationStats(stats)) {
 *   console.log(`Cache hit rate: ${stats.cacheHits / stats.totalRequests}`);
 * }
 * ```
 */
export function isDeduplicationStats(
  value: unknown
): value is DeduplicationStats {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const stats = value as Record<string, unknown>;

  return (
    typeof stats.totalRequests === 'number' &&
    typeof stats.deduplicatedRequests === 'number' &&
    typeof stats.cacheHits === 'number' &&
    typeof stats.cacheMisses === 'number' &&
    typeof stats.cacheSize === 'number' &&
    typeof stats.duplicateSavings === 'number' &&
    typeof stats.averageResponseTime === 'number'
  );
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if the given value is an AxiosError.
 *
 * @param error - The error to check
 * @returns True if the error is an AxiosError, false otherwise
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError)?.isAxiosError === true;
}

/**
 * Type guard to check if the given value is a NetworkError.
 *
 * @param error - The error to check
 * @returns True if the error is a NetworkError, false otherwise
 */
export function isNetworkError(error: unknown): boolean {
  return isAxiosError(error) && !error.response;
}

/**
 * Type guard to check if the given value is an HTTP error.
 *
 * @param error - The error to check
 * @returns True if the error is an HTTP error, false otherwise
 */
export function isHttpError(error: unknown): boolean {
  return isAxiosError(error) && !!error.response;
}

// =============================================================================
// DEDUPLICATION UTILITY FUNCTIONS
// =============================================================================

/**
 * Validates deduplication options and returns any validation errors.
 *
 * @param options - The options to validate
 * @returns Array of validation error messages (empty if valid)
 *
 * @example
 * ```typescript
 * const errors = validateDeduplicationOptions(options);
 * if (errors.length > 0) {
 *   console.error('Invalid options:', errors);
 * }
 * ```
 */
export function validateDeduplicationOptions(
  options: DeduplicationOptions
): string[] {
  const errors: string[] = [];

  if (options.ttl !== undefined) {
    if (options.ttl <= 0) {
      errors.push('ttl must be greater than 0');
    }
    if (options.ttl > 3600000) {
      // 1 hour max
      errors.push('ttl should not exceed 1 hour (3600000ms)');
    }
  }

  if (options.maxCacheSize !== undefined) {
    if (options.maxCacheSize <= 0) {
      errors.push('maxCacheSize must be greater than 0');
    }
    if (options.maxCacheSize > 10000) {
      errors.push('maxCacheSize should not exceed 10000 for memory safety');
    }
  }

  return errors;
}

/**
 * Creates a deduplication options object with defaults applied.
 *
 * @param overrides - Options to override the defaults
 * @returns Complete DeduplicationOptions with defaults
 *
 * @example
 * ```typescript
 * const options = createDeduplicationOptions({ ttl: 30000 });
 * // options.enabled === true (default)
 * // options.ttl === 30000 (override)
 * // options.maxCacheSize === 1000 (default)
 * ```
 */
export function createDeduplicationOptions(
  overrides?: Partial<DeduplicationOptions>
): DeduplicationOptions {
  return {
    ...DEFAULT_DEDUPLICATION_OPTIONS,
    ...overrides,
  };
}

/**
 * Creates an empty DeduplicationStats object.
 *
 * @returns Empty DeduplicationStats with all values at zero
 *
 * @example
 * ```typescript
 * const stats = createEmptyDeduplicationStats();
 * // Use as initial state for tracking
 * ```
 */
export function createEmptyDeduplicationStats(): DeduplicationStats {
  return {
    totalRequests: 0,
    deduplicatedRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    cacheSize: 0,
    duplicateSavings: 0,
    averageResponseTime: 0,
  };
}

/**
 * Calculates the deduplication savings percentage.
 *
 * @param stats - The deduplication statistics
 * @returns Percentage of requests that were deduplicated (0-100)
 *
 * @example
 * ```typescript
 * const savings = calculateDeduplicationSavings(stats);
 * console.log(`Saved ${savings.toFixed(1)}% of requests through deduplication`);
 * ```
 */
export function calculateDeduplicationSavings(
  stats: DeduplicationStats
): number {
  if (stats.totalRequests === 0) {
    return 0;
  }
  return (stats.deduplicatedRequests / stats.totalRequests) * 100;
}

// =============================================================================
// REQUEST COORDINATOR INTERFACE
// =============================================================================

/**
 * Notification event for the request coordinator.
 */
export interface CoordinatorNotificationEvent {
  /** Type of notification */
  type: NotificationType;
  /** Request ID associated with the event */
  requestId?: string;
  /** Human-readable message */
  message: string;
  /** Timestamp of the event */
  timestamp: number;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Types of notification events emitted by the request coordinator (retries,
 * circuit breaker transitions, deduplication outcomes, etc.).
 */
export enum NotificationType {
  RETRY_STARTED = 'retry_started',
  RETRY_ATTEMPT = 'retry_attempt',
  RETRY_SUCCESS = 'retry_success',
  RETRY_EXHAUSTED = 'retry_exhausted',
  CIRCUIT_BREAKER_OPENED = 'circuit_breaker_opened',
  CIRCUIT_BREAKER_CLOSED = 'circuit_breaker_closed',
  AUTH_REFRESH_REQUIRED = 'auth_refresh_required',
  REQUEST_CANCELLED = 'request_cancelled',
  PERFORMANCE_WARNING = 'performance_warning',
  REQUEST_DEDUPLICATED = 'request_deduplicated',
  DEDUPLICATION_CACHE_HIT = 'deduplication_cache_hit',
  DEDUPLICATION_CACHE_MISS = 'deduplication_cache_miss',
}

// =============================================================================
// REQUEST TRACKER INTERFACE
// =============================================================================

/**
 * Interface for the RequestTracker.
 *
 * Defines the contract for request lifecycle tracking including creation,
 * updates, completion, and performance metric retrieval.
 *
 * @example
 * ```typescript
 * const tracker: IRequestTracker = createRequestTracker(config);
 *
 * // Create and track a request
 * const context = tracker.createRequest(requestConfig);
 *
 * // Update request status
 * tracker.updateRequest(context.id, { status: RequestStatus.IN_PROGRESS });
 *
 * // Complete the request
 * tracker.completeRequest(context.id);
 *
 * // Get performance metrics
 * const metrics = tracker.getPerformanceMetrics(context.id);
 * ```
 */
export interface IRequestTracker {
  /**
   * Creates a new request context and starts tracking it.
   *
   * @param config - The request configuration
   * @returns The created request context
   */
  createRequest(config: RequestConfig): RequestContext;

  /**
   * Updates an existing request context.
   *
   * @param id - The request ID to update
   * @param update - The update to apply
   */
  updateRequest(id: string, update: RequestUpdate): void;

  /**
   * Marks a request as complete and moves it to completed requests.
   *
   * @param id - The request ID to complete
   */
  completeRequest(id: string): void;

  /**
   * Gets all currently active requests.
   *
   * @returns Array of active request contexts
   */
  getActiveRequests(): RequestContext[];

  /**
   * Gets a specific request by ID (active or completed).
   *
   * @param id - The request ID to retrieve
   * @returns The request context or undefined if not found
   */
  getRequest(id: string): RequestContext | undefined;

  /**
   * Cleans up old completed requests to prevent memory leaks.
   */
  cleanupCompletedRequests(): void;

  /**
   * Gets performance metrics for a specific request.
   *
   * @param id - The request ID to retrieve metrics for
   * @returns The performance metrics or undefined if not found
   */
  getPerformanceMetrics(id: string): PerformanceMetrics | undefined;

  /**
   * Gets all performance metrics.
   *
   * @returns Array of all performance metrics
   */
  getAllPerformanceMetrics(): PerformanceMetrics[];
}

// =============================================================================
// DEDUPLICATION MANAGER INTERFACE
// =============================================================================

/**
 * Deduplication manager interface for the request coordinator.
 */
export interface IDeduplicationManager {
  /** Execute a request with deduplication */
  execute<T>(
    key: DeduplicationKey,
    fn: () => Promise<T>
  ): Promise<DeduplicationResult<T>>;
  /** Cancel pending requests for a key */
  cancel(key: DeduplicationKey): boolean;
  /** Check if a request is pending */
  isPending(key: DeduplicationKey): boolean;
  /** Clear all cached entries */
  clear(): void;
  /** Get deduplication statistics */
  getStats(): DeduplicationStats;
  /** Update configuration */
  updateConfig(options: Partial<DeduplicationOptions>): void;
  /** Add a notification handler */
  addNotificationHandler(
    handler: (event: CoordinatorNotificationEvent) => void
  ): void;
  /** Subscribe to a pending request */
  subscribe<T>(key: DeduplicationKey): Promise<T> | null;
  /** Destroy the manager and clean up resources */
  destroy(): void;
}

/**
 * Simplified coordinator configuration for the request coordinator interface.
 * Used to avoid circular dependency with the full CoordinatorConfig type.
 */
export interface ICoordinatorConfig {
  /** Retry configuration */
  retry?: unknown;
  /** Deduplication configuration */
  deduplication?: DeduplicationOptions;
  /** Whether to enable performance monitoring */
  enablePerformanceMonitoring?: boolean;
  /** Whether to enable circuit breaker protection */
  enableCircuitBreaker?: boolean;
  /** Whether to enable notification system */
  enableNotifications?: boolean;
  /** Whether to enable logging */
  enableLogging?: boolean;
  /** Interval for memory cleanup in milliseconds */
  memoryCleanupInterval?: number;
}

/**
 * Simplified coordination result for the request coordinator interface.
 * Used to avoid circular dependency with the full CoordinationResult type.
 */
export interface ICoordinationResult {
  /** Whether the operation was successful */
  success: boolean;
  /** Response data if successful */
  response?: AxiosResponse;
  /** Error if failed */
  error?: Error;
  /** Request context */
  context: RequestContext;
  /** Performance metrics */
  metrics: {
    requestId: string;
    startTime: number;
    retryCount: number;
  };
}

/**
 * Interface for the RequestCoordinator.
 *
 * Defines the contract for the central orchestrator that manages the complete
 * request lifecycle including retries, authentication, error handling,
 * deduplication, and metrics integration.
 *
 * @example
 * ```typescript
 * const coordinator: IRequestCoordinator = createRequestCoordinator(axiosClient, config);
 *
 * // Execute a request through the coordinator
 * const response = await coordinator.executeRequest({
 *   url: '/api/users',
 *   method: 'GET',
 * });
 *
 * // Get active requests
 * const activeRequests = coordinator.getActiveRequests();
 *
 * // Clean up when done
 * coordinator.destroy();
 * ```
 */
export interface IRequestCoordinator {
  /**
   * Executes a request through the unified coordination system with deduplication.
   *
   * @param config - The request configuration
   * @returns Promise resolving to the Axios response
   */
  executeRequest(config: RequestConfig): Promise<AxiosResponse>;

  /**
   * Handles successful responses by adding metadata if performance monitoring is enabled.
   *
   * @param response - The Axios response to process
   * @returns The processed response
   */
  handleResponse(response: AxiosResponse): AxiosResponse;

  /**
   * Central error handling with intelligent routing based on error classification.
   * Routes errors to auth handling, retry handling, or transformation as appropriate.
   *
   * @param error - The error that occurred
   * @param context - The request context
   * @returns Promise resolving to the coordination result
   */
  handleError(
    error: Error,
    context: RequestContext
  ): Promise<ICoordinationResult>;

  /**
   * Updates coordinator configuration including deduplication settings.
   *
   * @param config - Partial configuration to merge with existing config
   */
  updateConfig(config: Partial<ICoordinatorConfig>): void;

  /**
   * Gets currently active requests being tracked by the coordinator.
   *
   * @returns Array of active request contexts
   */
  getActiveRequests(): RequestContext[];

  /**
   * Gets performance and operational metrics including deduplication stats.
   *
   * @returns Object containing tracker, performance, circuit breaker, and deduplication metrics
   */
  getMetrics(): {
    summary: IAxiosIntegrationMetrics;
    tracker: Record<string, unknown>;
    performance: unknown[];
    circuitBreakers: Map<string, unknown>;
    deduplication: DeduplicationStats;
  };

  /**
   * Adds a notification handler for coordinator events.
   *
   * @param handler - Function to call when events occur
   */
  addNotificationHandler(
    handler: (event: CoordinatorNotificationEvent) => void
  ): void;

  /**
   * Removes a previously added notification handler.
   *
   * @param handler - The handler function to remove
   */
  removeNotificationHandler(
    handler: (event: CoordinatorNotificationEvent) => void
  ): void;

  /**
   * Cleans up resources including deduplication manager and notification handlers.
   */
  destroy(): void;

  /**
   * Gets the deduplication manager for advanced operations.
   *
   * @returns The deduplication manager instance
   */
  getDeduplicationManager(): IDeduplicationManager;

  /**
   * Cancels all pending requests for a given deduplication key.
   *
   * @param key - The deduplication key
   * @returns True if requests were cancelled
   */
  cancelPendingRequests(key: DeduplicationKey): boolean;

  /**
   * Checks if a request is currently pending deduplication.
   *
   * @param key - The deduplication key
   * @returns True if a request is pending
   */
  isRequestPending(key: DeduplicationKey): boolean;

  /**
   * Clears all deduplicated requests from cache.
   */
  clearDeduplicationCache(): void;

  /**
   * Resets all circuit breaker state (open/failure counts). Primarily for test
   * isolation where a shared client would otherwise carry breaker state.
   */
  resetCircuitBreaker(): void;
}

// =============================================================================
// AXIOS INTEGRATION INTERFACE
// =============================================================================

/**
 * Simplified Axios integration metrics for the interface.
 */
export interface IAxiosIntegrationMetrics {
  /** Total number of requests processed */
  totalRequests: number;
  /** Number of successful requests */
  successfulRequests: number;
  /** Number of failed requests */
  failedRequests: number;
  /** Number of requests that were retried */
  retriedRequests: number;
  /** Average response time in milliseconds */
  averageResponseTime: number;
}

/**
 * Simplified Axios integration configuration for the interface.
 */
export interface IAxiosIntegrationConfig {
  /** Integration layer configuration */
  integration?: unknown;
  /** Enable legacy mode for backward compatibility */
  enableLegacyMode?: boolean;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Interface for the AxiosIntegration class.
 *
 * Defines the contract for the Axios integration layer that provides seamless
 * integration between Axios and the unified request coordinator system.
 *
 * @example
 * ```typescript
 * const integration: IAxiosIntegration = createAxiosIntegration(axiosClient, config);
 *
 * // Setup interceptors
 * integration.setupInterceptors();
 *
 * // Get metrics
 * const metrics = integration.getMetrics();
 * console.log('Total requests:', metrics.totalRequests);
 *
 * // Cleanup when done
 * integration.cleanup();
 * ```
 */
export interface IAxiosIntegration {
  /**
   * Setup interceptors and integrate coordinator with Axios.
   * This method configures request/response interceptors for metadata tracking
   * and response metrics collection.
   */
  setupInterceptors(): void;

  /**
   * Create and return a new integrated request coordinator.
   *
   * @param config - Optional coordinator configuration
   * @returns A new request coordinator instance
   */
  createRequestCoordinator(
    config?: Partial<ICoordinatorConfig>
  ): IRequestCoordinator;

  /**
   * Preserve existing behavior while integrating new system.
   * This is a no-op when backward compatibility is not needed.
   */
  preserveExistingBehavior(): void;

  /**
   * Update integration configuration.
   *
   * @param config - Partial configuration to merge with existing config
   */
  updateConfig(config: Partial<IAxiosIntegrationConfig>): void;

  /**
   * Get current integration metrics.
   *
   * @returns Copy of the current metrics
   */
  getMetrics(): IAxiosIntegrationMetrics;

  /**
   * Get active requests being tracked by the integration.
   *
   * @returns Array of active request contexts
   */
  getActiveRequests(): RequestContext[];

  /**
   * Get the internal request coordinator.
   *
   * Use this to subscribe to coordinator notifications or access
   * coordinator features. Note: createRequestCoordinator() creates
   * a NEW coordinator, while this returns the actual internal coordinator
   * that handles requests.
   *
   * @returns The internal request coordinator instance
   */
  getCoordinator(): IRequestCoordinator;

  /**
   * Cleanup integration and remove interceptors.
   * Removes all registered interceptors and clears active request tracking.
   */
  cleanup(): void;
}
